import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { canAccessAnalytics } from '../utils/accessControl';

const SimpleDashboard = () => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is logged in
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setToken(storedToken);
        setUser(userData);
        console.log('✅ User loaded from localStorage:', userData);
      } catch (error) {
        console.error('❌ Error parsing user data:', error);
        handleLogout();
      }
    } else {
      console.log('❌ No stored credentials found');
      navigate('/direct-login');
    }
    
    setLoading(false);
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/direct-login');
  };

  const testApiCall = async () => {
    try {
      const response = await fetch('/api/courses/', {
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        alert(`✅ API Test Successful! Found ${data.count} courses.`);
      } else {
        alert(`❌ API Test Failed: ${response.status}`);
      }
    } catch (error) {
      alert(`❌ API Test Error: ${error.message}`);
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5'
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5'
      }}>
        <div>Redirecting to login...</div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f5f5f5',
      padding: '2rem'
    }}>
      {/* Header */}
      <div style={{
        backgroundColor: 'white',
        padding: '1rem 2rem',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '2rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <h1 style={{ margin: 0, color: '#1a1a1a' }}>
            Welcome, {user.first_name} {user.last_name}!
          </h1>
          <p style={{ margin: '0.5rem 0 0 0', color: '#666' }}>
            {user.email} {user.is_staff && '(Staff)'}
          </p>
        </div>
        <button
          onClick={handleLogout}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: '#dc3545',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Logout
        </button>
      </div>

      {/* User Info Card */}
      <div style={{
        backgroundColor: 'white',
        padding: '2rem',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '2rem'
      }}>
        <h2 style={{ marginTop: 0, color: '#1a1a1a' }}>User Information</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
          <div>
            <strong>Email:</strong> {user.email}
          </div>
          <div>
            <strong>Name:</strong> {user.first_name} {user.last_name}
          </div>
          <div>
            <strong>Staff Status:</strong> {user.is_staff ? '✅ Staff' : '❌ Regular User'}
          </div>
          <div>
            <strong>Superuser:</strong> {user.is_superuser ? '✅ Yes' : '❌ No'}
          </div>
          <div>
            <strong>Verified:</strong> {user.is_verified ? '✅ Yes' : '❌ No'}
          </div>
          <div>
            <strong>Active:</strong> {user.is_active ? '✅ Yes' : '❌ No'}
          </div>
        </div>
      </div>

      {/* Token Info */}
      <div style={{
        backgroundColor: 'white',
        padding: '2rem',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '2rem'
      }}>
        <h2 style={{ marginTop: 0, color: '#1a1a1a' }}>Authentication Token</h2>
        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '1rem',
          borderRadius: '4px',
          fontFamily: 'monospace',
          fontSize: '0.9rem',
          wordBreak: 'break-all'
        }}>
          {token}
        </div>
      </div>

      {/* Actions */}
      <div style={{
        backgroundColor: 'white',
        padding: '2rem',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '2rem'
      }}>
        <h2 style={{ marginTop: 0, color: '#1a1a1a' }}>Quick Actions</h2>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <button
            onClick={testApiCall}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Test API Call
          </button>
          
          <button
            onClick={() => window.location.href = '/courses'}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#17a2b8',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            View Courses
          </button>

          {canAccessAnalytics(user) && (
            <>
              <button
                onClick={() => window.location.href = '/analytics'}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#FFD700',
                  color: '#1a1a1a',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Analytics Dashboard
              </button>
              
              <button
                onClick={() => window.open('http://localhost:8000/admin/', '_blank')}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Django Admin
              </button>
            </>
          )}
        </div>
      </div>

      {/* Success Message */}
      <div style={{
        backgroundColor: '#d4edda',
        color: '#155724',
        padding: '1rem',
        borderRadius: '8px',
        border: '1px solid #c3e6cb'
      }}>
        <strong>✅ Direct Login Successful!</strong>
        <p style={{ margin: '0.5rem 0 0 0' }}>
          You are now logged in using the direct login method. 
          This bypasses the complex AuthContext and uses simple localStorage for session management.
        </p>
      </div>
    </div>
  );
};

export default SimpleDashboard;
